import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isBefore, isToday, startOfDay } from 'date-fns';
import './CallbackList.css';

const emptyForm = { first_name: '', last_name: '', phone: '', email: '', description: '', callback_date: '', customer_id: null };

const CallbackList = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const nameFieldRef = useRef(null);

  useEffect(() => {
    fetchEntries();
    fetchCustomers();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (nameFieldRef.current && !nameFieldRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchEntries = async () => {
    try {
      const res = await fetch('/api/callback-list?show_contacted=true');
      const data = await res.json();
      setEntries(data);
    } catch (error) {
      console.error('Error fetching callback list:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await fetch('/api/customers');
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const updateFormField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value, customer_id: null }));
  };

  const handleSelectCustomer = (c) => {
    setFormData((prev) => ({
      ...prev,
      first_name: c.first_name,
      last_name: c.last_name || '',
      phone: c.phone || '',
      email: c.email || '',
      customer_id: c.id
    }));
    setShowSuggestions(false);
  };

  const query = formData.first_name.trim().toLowerCase();
  const customerSuggestions = query.length >= 2
    ? customers.filter(c =>
        `${c.first_name} ${c.last_name} ${c.phone || ''}`.toLowerCase().includes(query)
      ).slice(0, 8)
    : [];

  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/callback-list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setFormData(emptyForm);
        fetchEntries();
      }
    } catch (error) {
      console.error('Error adding callback entry:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateEntry = async (id) => {
    try {
      const res = await fetch(`/api/callback-list/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });
      if (res.ok) {
        setEditingId(null);
        fetchEntries();
      }
    } catch (error) {
      console.error('Error updating callback entry:', error);
    }
  };

  const handleToggleContacted = async (id, isContacted) => {
    try {
      await fetch(`/api/callback-list/${id}/contacted`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_contacted: !isContacted })
      });
      fetchEntries();
    } catch (error) {
      console.error('Error toggling contacted status:', error);
    }
  };

  const handleConvertToCustomer = async (entryId) => {
    try {
      const res = await fetch(`/api/callback-list/${entryId}/convert`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        alert(`Successfully converted to customer! Customer ID: ${data.customer.id}`);
        fetchEntries();
      } else {
        alert(`Failed to convert to customer: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error converting to customer:', error);
      alert('Failed to convert to customer: Network error');
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Delete this callback entry?')) return;
    try {
      await fetch(`/api/callback-list/${id}`, { method: 'DELETE' });
      fetchEntries();
    } catch (error) {
      console.error('Error deleting callback entry:', error);
    }
  };

  const getDateBadge = (dateStr) => {
    const date = startOfDay(parseISO(dateStr));
    const today = startOfDay(new Date());
    if (isBefore(date, today)) {
      return { label: `Overdue — ${format(date, 'MMM d, yyyy')}`, className: 'callback-badge--overdue' };
    }
    if (isToday(date)) {
      return { label: 'Today', className: 'callback-badge--today' };
    }
    return { label: format(date, 'MMM d, yyyy'), className: 'callback-badge--upcoming' };
  };

  const pendingEntries = entries.filter(e => !e.is_contacted);
  const contactedEntries = entries.filter(e => e.is_contacted);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="callback-list-page">
      <div className="page-header">
        <h1 className="page-header__title">Callback List</h1>
      </div>

      <div className="callback-list-layout">

        {/* Add Entry Panel */}
        <div className="callback-list-add-panel">
          <h2 className="callback-list-panel-title">Add to Callback List</h2>
          <form onSubmit={handleAddEntry}>
            <div className="form-group" ref={nameFieldRef} style={{ position: 'relative' }}>
              <label className="form-label">First Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.first_name}
                onChange={(e) => updateFormField('first_name', e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                autoComplete="off"
                placeholder="Start typing to search existing customers..."
                required
              />
              {showSuggestions && !formData.customer_id && customerSuggestions.length > 0 && (
                <ul className="callback-suggestions">
                  {customerSuggestions.map((c) => (
                    <li key={c.id} onMouseDown={() => handleSelectCustomer(c)}>
                      <span className="callback-suggestions__name">{c.first_name} {c.last_name}</span>
                      {c.phone && <span className="callback-suggestions__phone">{c.phone}</span>}
                    </li>
                  ))}
                </ul>
              )}
              {formData.customer_id && (
                <div className="callback-linked-chip">
                  <span>✓ Linked to existing customer</span>
                  <button type="button" onClick={() => setFormData((prev) => ({ ...prev, customer_id: null }))}>Unlink</button>
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.last_name}
                onChange={(e) => updateFormField('last_name', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="tel"
                className="form-input"
                value={formData.phone}
                onChange={(e) => updateFormField('phone', e.target.value)}
                placeholder="(555) 555-5555"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={formData.email}
                onChange={(e) => updateFormField('email', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">What are they interested in?</label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                placeholder="e.g., Full detail on the Tahoe, waiting until back from vacation..."
              />
            </div>
            <div className="form-group">
              <label className="form-label">Recontact On *</label>
              <input
                type="date"
                className="form-input"
                value={formData.callback_date}
                onChange={(e) => setFormData({ ...formData, callback_date: e.target.value })}
                required
              />
            </div>
            <button type="submit" className="btn btn--primary callback-list-submit" disabled={submitting}>
              {submitting ? 'Adding...' : '+ Add to List'}
            </button>
          </form>
        </div>

        {/* List Panel */}
        <div className="callback-list-list-panel">

          {/* Tabs */}
          <div className="callback-list-tabs">
            <button
              type="button"
              className={`callback-list-tab ${tab === 'pending' ? 'callback-list-tab--active' : ''}`}
              onClick={() => setTab('pending')}
            >
              To Contact ({pendingEntries.length})
            </button>
            <button
              type="button"
              className={`callback-list-tab ${tab === 'contacted' ? 'callback-list-tab--active' : ''}`}
              onClick={() => setTab('contacted')}
            >
              Contacted ({contactedEntries.length})
            </button>
          </div>

          {/* Pending List */}
          {tab === 'pending' && (
            <div className="callback-list-items">
              {pendingEntries.length === 0 ? (
                <p className="callback-list-empty">No one on the callback list right now.</p>
              ) : pendingEntries.map((entry) => {
                const badge = getDateBadge(entry.callback_date);
                return (
                  <div
                    key={entry.id}
                    className={`callback-list-item ${editingId === entry.id ? 'callback-list-item--editing' : ''}`}
                  >
                    {editingId === entry.id ? (
                      <div className="callback-list-item__edit">
                        <input
                          type="text"
                          className="form-input"
                          value={editData.first_name}
                          onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                          placeholder="First name"
                        />
                        <input
                          type="text"
                          className="form-input"
                          value={editData.last_name}
                          onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                          placeholder="Last name"
                        />
                        <input
                          type="tel"
                          className="form-input"
                          value={editData.phone}
                          onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                          placeholder="Phone"
                        />
                        <input
                          type="email"
                          className="form-input"
                          value={editData.email}
                          onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                          placeholder="Email"
                        />
                        <textarea
                          className="form-textarea"
                          value={editData.description}
                          onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                          rows="2"
                          placeholder="Interested in..."
                        />
                        <input
                          type="date"
                          className="form-input"
                          value={editData.callback_date}
                          onChange={(e) => setEditData({ ...editData, callback_date: e.target.value })}
                        />
                        <div className="callback-list-item__actions">
                          <button type="button" className="btn btn--primary btn--small" onClick={() => handleUpdateEntry(entry.id)}>Save</button>
                          <button type="button" className="btn btn--secondary btn--small" onClick={() => setEditingId(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="callback-list-item__view">
                        <div className="callback-list-item__info">
                          <div className="callback-list-item__name-row">
                            <span className="callback-list-item__name">{entry.first_name} {entry.last_name}</span>
                            {entry.customer_id && <span className="callback-badge callback-badge--customer">Existing Customer</span>}
                            <span className={`callback-badge ${badge.className}`}>{badge.label}</span>
                          </div>
                          {(entry.phone || entry.email) && (
                            <div className="callback-list-item__contact">
                              {entry.phone && <span>{entry.phone}</span>}
                              {entry.phone && entry.email && <span> · </span>}
                              {entry.email && <span>{entry.email}</span>}
                            </div>
                          )}
                          {entry.description && <div className="callback-list-item__meta">{entry.description}</div>}
                        </div>
                        <div className="callback-list-item__actions">
                          <button
                            type="button"
                            className="btn btn--secondary btn--small"
                            onClick={() => {
                              setEditingId(entry.id);
                              setEditData({
                                first_name: entry.first_name || '',
                                last_name: entry.last_name || '',
                                phone: entry.phone || '',
                                email: entry.email || '',
                                description: entry.description || '',
                                callback_date: entry.callback_date || ''
                              });
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn--primary btn--small"
                            onClick={() => handleToggleContacted(entry.id, entry.is_contacted)}
                          >
                            Mark Contacted
                          </button>
                          {entry.customer_id ? (
                            <button
                              type="button"
                              className="btn btn--secondary btn--small"
                              onClick={() => navigate(`/customers/${entry.customer_id}`)}
                            >
                              View Customer
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn--secondary btn--small"
                              onClick={() => handleConvertToCustomer(entry.id)}
                            >
                              Convert to Customer
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn--danger btn--small"
                            onClick={() => handleDeleteEntry(entry.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Contacted List */}
          {tab === 'contacted' && (
            <div className="callback-list-items">
              {contactedEntries.length === 0 ? (
                <p className="callback-list-empty">No contacted entries yet.</p>
              ) : contactedEntries.map((entry) => (
                <div key={entry.id} className="callback-list-item callback-list-item--contacted">
                  <div className="callback-list-item__view">
                    <div className="callback-list-item__info">
                      <span className="callback-list-item__name callback-list-item__name--struck">{entry.first_name} {entry.last_name}</span>
                      {(entry.phone || entry.email) && (
                        <div className="callback-list-item__contact">
                          {entry.phone && <span>{entry.phone}</span>}
                          {entry.phone && entry.email && <span> · </span>}
                          {entry.email && <span>{entry.email}</span>}
                        </div>
                      )}
                      {entry.description && <div className="callback-list-item__meta">{entry.description}</div>}
                      {entry.contacted_at && (
                        <div className="callback-list-item__date">
                          Contacted: {format(new Date(entry.contacted_at), 'MMM d, yyyy')}
                        </div>
                      )}
                    </div>
                    <div className="callback-list-item__actions">
                      <button
                        type="button"
                        className="btn btn--secondary btn--small"
                        onClick={() => handleToggleContacted(entry.id, entry.is_contacted)}
                      >
                        Move Back
                      </button>
                      {entry.customer_id ? (
                        <button
                          type="button"
                          className="btn btn--secondary btn--small"
                          onClick={() => navigate(`/customers/${entry.customer_id}`)}
                        >
                          View Customer
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn--secondary btn--small"
                          onClick={() => handleConvertToCustomer(entry.id)}
                        >
                          Convert to Customer
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn--danger btn--small"
                        onClick={() => handleDeleteEntry(entry.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default CallbackList;
